import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import "./metaPanel.css";

/*
 * MetaPanel — the "Meta" tab of the document detail pane.
 *
 * Replaces the old flat card list (every field an equal-weight ~110px card, raw
 * upper-case column names, storage internals interleaved with business data).
 *
 * The server flattens EVERY physical column of the content table that is not a
 * known DocItem property into `searchIndexes` (DBOperations.SearchContent) — so
 * customer index columns (FieldInfo) and storage plumbing (StorageFileId,
 * Position, CompressType…) arrive in one undifferentiated bag of strings.
 * We separate them client-side using the FieldInfo metadata the search context
 * already holds (GetDatabases → relatedIndexes), which also gives us the
 * customer's own display name and field order for each index column:
 *
 *   Index Fields      — customer-defined searchable columns, ordered, open
 *   Document          — name / type / dates / status / channel / sender, open
 *   Additional        — anything unclassified, open
 *   Storage & System  — storage file id, position, compress type, uuids, closed
 *
 * Raw key and raw value always stay reachable (label tooltip, per-row copy,
 * JSON view) so support staff lose nothing to the friendlier presentation.
 */

const Icon = {
  search: (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  ),
  copy: (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  ),
  // Braces as a glyph rather than a path: at 13px the curved SVG version reads as parentheses.
  json: <span className="mp-glyph" aria-hidden="true">{"{ }"}</span>,
  chevron: (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="9 6 15 12 9 18" />
    </svg>
  ),
  list: (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" />
      <circle cx="4" cy="6" r="1" /><circle cx="4" cy="12" r="1" /><circle cx="4" cy="18" r="1" />
    </svg>
  ),
  check: (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  ),
};

/* ── Field classification ──────────────────────────────────────────────── */

// Normalize a key for matching: "Storage_File-Id" / "StorageFileId" → "storagefileid"
export function normKey(k) {
  return String(k).toLowerCase().replace(/[^a-z0-9]/g, "");
}

/*
 * Every content table is created from one DDL (DBOperations.BuildPlainTableSql and
 * its partitioned variants), so its fixed columns are known exactly — and anything
 * in `searchIndexes` that is NOT one of them is, by construction, a column the
 * customer added through AddDatabaseIndexes. That is the discriminator: a
 * FieldInfo row is NOT one, because the fixed columns (ArchiveNo, Sender, Status,
 * Channel, DocumentUUID…) are registered as FieldInfo rows too — FieldInfo only
 * supplies the display name, data type and ordering.
 */

// Fixed columns that describe storage/plumbing, plus DocItem transport identifiers.
const SYSTEM_KEYS = new Set([
  // content-table DDL
  "id", "storagefileid", "position", "compresstype", "documentuuid",
  "storageinfoid", "fileoffset", "compressedsize", "isdeleted",
  // columns added by later pillars (suppressed today, tolerated if they surface)
  "versiongroupid", "versionnumber", "iscurrentversion", "recordcategoryid",
  "folderid", "taxonomyid", "contenttypeid", "dbinfoid", "filetypeid",
  // DocItem / generic plumbing
  "itemid", "link", "rowid", "uuid", "storagepath", "archivepath", "filepath", "path",
  "offset", "blocksize", "blockcount", "compression", "batchid", "processid", "processtype",
  "partitionname", "partition", "tablename", "schemaname",
  "hash", "checksum", "md5", "sha1", "sha256", "crc", "deleted",
]);

// Fixed columns that describe the document itself: what it is, where it came from, its state.
const DOCUMENT_KEYS = new Set([
  // content-table DDL
  "originalfilename", "fileextension", "pagecount", "archiveno", "documentdate",
  "sender", "channel", "status", "documentname",
  // DocItem properties / common equivalents
  "originalname", "filename", "customerno", "dbname", "name", "title", "description",
  "extension", "filetype", "documenttype", "mimetype", "contenttype", "pages",
  "filesize", "size", "sizebytes", "totalsize", "originalsize",
  "date", "createddate", "createdat", "created", "uploaddate", "uploadedat",
  "modifieddate", "modifiedat", "updatedat", "archivedate", "archivedat", "recordcutoffdate",
  "state", "receiver", "owner", "author", "username", "user",
  "database", "databasename", "organization", "orgcode", "language",
]);

// Transport plumbing that is never metadata about the document.
const HIDDEN_KEYS = new Set(["outputtypelist", "downloadlinks", "searchindexes"]);

const GROUP_INDEX = "index";
const GROUP_DOCUMENT = "document";
const GROUP_OTHER = "other";
const GROUP_SYSTEM = "system";

// Group order on screen; `open` is the initial expanded state.
const GROUP_ORDER = [
  { id: GROUP_INDEX, i18n: "meta.groupIndex", fallback: "Index Fields", open: true },
  { id: GROUP_DOCUMENT, i18n: "meta.groupDocument", fallback: "Document", open: true },
  { id: GROUP_OTHER, i18n: "meta.groupOther", fallback: "Additional", open: true },
  { id: GROUP_SYSTEM, i18n: "meta.groupSystem", fallback: "Storage & System", open: false },
];

/* ── Value formatting ──────────────────────────────────────────────────── */

const DATE_KEY_RE = /(date|tarih|zaman|datum|fecha|time|created|modified|updated)/i;
const SIZE_KEY_RE = /(size|bytes|boyut)/i;
const MONO_KEY_RE = /(uuid|guid|hash|checksum|md5|sha|position|offset|link|itemid|path|token)/i;
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}([T ]\d{2}:\d{2}(:\d{2})?)?/;
// Values that must never be regrouped with thousands separators (account numbers, IBANs…).
const IDENTIFIER_KEY_RE = /(no$|number$|num$|code$|account|iban|phone|tel|card|ref)/i;
// FieldInfo.DataType values seen in the field: "int", "number", "DATE", "datetime",
// "CHAR", "string", "Text" — only the first family may be regrouped as a number.
const NUMERIC_TYPE_RE = /^(int|number|numeric|decimal|bigint|smallint|tinyint|float|double|long|money)/;

const STATUS_TONE = [
  { re: /(archiv|complete|success|active|approved|done|declared)/i, tone: "ok" },
  { re: /(pending|processing|running|queued|waiting|progress|proposed)/i, tone: "warn" },
  { re: /(fail|error|deleted|destroyed|rejected|cancel|expired)/i, tone: "bad" },
  { re: /(hold|frozen|locked|suspend)/i, tone: "info" },
];

function humanBytes(n) {
  const b = Number(n);
  if (!isFinite(b) || b < 0) return null;
  if (b < 1024) return `${b} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let v = b / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i += 1; }
  return `${v.toFixed(v < 10 ? 1 : 0)} ${units[i]}`;
}

function isObjectLike(v) {
  return v && typeof v === "object" && !(v instanceof Date);
}

export function rawString(v) {
  if (v == null) return "";
  if (typeof v === "string") return v;
  if (isObjectLike(v)) {
    try { return JSON.stringify(v, null, 2); } catch { return String(v); }
  }
  return String(v);
}

/*
 * Render a value for reading. Returns { text, mono, tone, hint, muted, block }.
 * `dataType` is the FieldInfo DataType when the column is a customer index.
 */
function formatValue(key, value, locale, dataType) {
  const raw = rawString(value);
  if (raw.trim() === "" || raw === "-") return { text: "—", muted: true };

  if (isObjectLike(value)) return { text: raw, mono: true, block: true };

  const n = normKey(key);
  const dt = String(dataType || "").toLowerCase();

  if (typeof value === "boolean") return { text: raw, tone: value ? "ok" : "muted" };

  // Bit columns arrive as "0"/"1"/"True"/"False" strings.
  if (/^(is|has)[A-Z_]/.test(key) && /^(0|1|true|false)$/i.test(raw)) {
    const on = /^(1|true)$/i.test(raw);
    return { text: on ? "Yes" : "No", tone: on ? "ok" : "muted", hint: raw };
  }

  if (/^(status|state|durum)$/.test(n) || n.endsWith("status")) {
    const hit = STATUS_TONE.find((s) => s.re.test(raw));
    return { text: raw, tone: hit ? hit.tone : "info" };
  }

  if (SIZE_KEY_RE.test(key) && /^\d+$/.test(raw)) {
    const human = humanBytes(raw);
    if (human) return { text: human, hint: `${Number(raw).toLocaleString(locale)} B` };
  }

  if ((DATE_KEY_RE.test(key) || dt.startsWith("date")) && ISO_DATE_RE.test(raw)) {
    const parsed = new Date(raw);
    if (!isNaN(parsed)) {
      const hasTime = /[T ]\d{2}:\d{2}/.test(raw) && !/[T ]00:00(:00)?(\.0+)?$/.test(raw);
      const text = hasTime
        ? parsed.toLocaleString(locale, { year: "numeric", month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" })
        : parsed.toLocaleDateString(locale, { year: "numeric", month: "short", day: "2-digit" });
      return { text, hint: raw };
    }
  }

  if (MONO_KEY_RE.test(key) || (/^[0-9a-f]{8}-[0-9a-f-]{8,}$/i.test(raw))) {
    return { text: raw, mono: true };
  }

  // Long integers read better grouped, but ONLY when the column is declared numeric:
  // customer bases routinely store account/card/merchant numbers as string columns,
  // and "4.402.118.366.401" would be wrong there in a way the user cannot undo.
  if (/^\d{5,}$/.test(raw) && NUMERIC_TYPE_RE.test(dt) && !IDENTIFIER_KEY_RE.test(n)) {
    return { text: Number(raw).toLocaleString(locale), hint: raw };
  }

  return { text: raw };
}

/*
 * A few DocItem properties are renamed copies of a physical column, or humanize
 * badly ("dbName" → "Db Name"). `alias` points the key at the physical column so
 * it inherits that column's FieldInfo display name; `label` is the fallback when
 * there is no FieldInfo to inherit.
 */
const KEY_OVERRIDES = {
  customerno: { alias: "archiveno", label: "Archive No." },   // DocItem.CustomerNo IS ArchiveNo
  originalname: { alias: "originalfilename", label: "Original Name" },
  dbname: { label: "Database" },
  itemid: { label: "Item Token" },
  pagecount: { label: "Pages" },
};

// "storage_file_id" / "documentUuid" → readable label. ALL-CAPS customer column
// names are left alone: that spelling is what the user searches by.
export function labelize(key) {
  const s = String(key);
  if (/^[A-Z0-9_]+$/.test(s) && s.length > 1) return s.replace(/_+/g, " ").trim();
  return s
    .replace(/[_-]+/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^./, (c) => c.toUpperCase());
}

/*
 * Build normalizedKey → { label, dataType, order } from the FieldInfo rows the
 * search context caches (GetDatabases.relatedIndexes). Absent/failed metadata
 * simply yields an empty map and the panel falls back to name heuristics.
 */
export function buildFieldMeta(dbMeta, dbName) {
  const map = new Map();
  if (!Array.isArray(dbMeta) || !dbName) return map;
  const want = String(dbName).toLowerCase();
  const entry = dbMeta.find(
    (d) => String(d?.name || "").toLowerCase() === want || String(d?.displayName || "").toLowerCase() === want,
  );
  const fields = entry?.relatedIndexes || entry?.RelatedIndexes;
  if (!Array.isArray(fields)) return map;
  for (const f of fields) {
    const fieldName = f?.fieldName ?? f?.FieldName;
    if (!fieldName) continue;
    const display = f?.fieldDisplayName ?? f?.FieldDisplayName;
    map.set(normKey(fieldName), {
      label: (display && String(display).trim()) || labelize(fieldName),
      dataType: f?.dataType ?? f?.DataType ?? "",
      order: Number(f?.fieldOrder ?? f?.FieldOrder ?? 0) || 0,
    });
  }
  return map;
}

/* ── Component ─────────────────────────────────────────────────────────── */

export default function MetaPanel({ doc, indexObj, indexKey, skipKeys, fieldMeta }) {
  const { t, i18n } = useTranslation();
  const locale = i18n?.language || undefined;

  const [query, setQuery] = useState("");
  const [jsonMode, setJsonMode] = useState(false);
  const [showEmpty, setShowEmpty] = useState(false);
  const [copied, setCopied] = useState(null); // "all" | field key
  const [overrides, setOverrides] = useState({}); // groupId → forced open/closed

  // Flatten the document + its nested index object into one classified field list.
  const { fields, emptyCount } = useMemo(() => {
    const out = [];
    const seen = new Set();
    const skip = skipKeys instanceof Set ? skipKeys : new Set(skipKeys || []);
    const meta = fieldMeta instanceof Map ? fieldMeta : new Map();
    let empties = 0;

    const push = (key, value, fromIndex) => {
      const nk = normKey(key);
      if (seen.has(nk) || skip.has(key) || HIDDEN_KEYS.has(nk)) return;
      if (Array.isArray(value) && value.length === 0) return;
      const blank = value == null || rawString(value).trim() === "";
      if (blank) {
        empties += 1;
        if (!showEmpty) return;
      }
      seen.add(nk);

      // FieldInfo gives the label/type/order for ANY column, fixed or customer-added;
      // the group comes from whether the column is part of the fixed table shape.
      const ov = KEY_OVERRIDES[nk];
      const fi = meta.get(nk) || (ov?.alias ? meta.get(ov.alias) : undefined);
      let group;
      if (SYSTEM_KEYS.has(nk)) group = GROUP_SYSTEM;
      else if (DOCUMENT_KEYS.has(nk)) group = GROUP_DOCUMENT;
      else if (fromIndex) group = GROUP_INDEX;         // not a fixed column → customer index
      else group = GROUP_OTHER;

      out.push({
        key,
        label: fi?.label || ov?.label || labelize(key),
        dataType: fi?.dataType || "",
        order: fi?.order ?? 0,
        value,
        group,
      });
    };

    // Customer index columns first so they win de-duplication against doc-level copies.
    if (indexObj && typeof indexObj === "object") {
      for (const [k, v] of Object.entries(indexObj)) push(k, v, true);
    }
    for (const [k, v] of Object.entries(doc || {})) {
      if (indexKey && k === indexKey) continue;
      if (isObjectLike(v) && !Array.isArray(v)) continue; // nested blobs stay in JSON view
      push(k, v, false);
    }

    // Index columns follow the customer's configured field order.
    out.sort((a, b) => {
      if (a.group !== GROUP_INDEX || b.group !== GROUP_INDEX) return 0;
      return (a.order - b.order) || a.label.localeCompare(b.label);
    });
    return { fields: out, emptyCount: empties };
  }, [doc, indexObj, indexKey, skipKeys, fieldMeta, showEmpty]);

  const q = query.trim().toLowerCase();
  const matches = useMemo(() => {
    if (!q) return fields;
    return fields.filter(
      (f) =>
        f.label.toLowerCase().includes(q) ||
        f.key.toLowerCase().includes(q) ||
        rawString(f.value).toLowerCase().includes(q),
    );
  }, [fields, q]);

  const groups = useMemo(
    () =>
      GROUP_ORDER.map((g) => ({
        ...g,
        items: matches.filter((f) => f.group === g.id),
        total: fields.filter((f) => f.group === g.id).length,
      })).filter((g) => g.items.length > 0),
    [matches, fields],
  );

  const rawJson = useMemo(() => {
    const obj = {};
    for (const f of matches) obj[f.key] = f.value;
    try { return JSON.stringify(obj, null, 2); } catch { return String(obj); }
  }, [matches]);

  const copyText = (text, id) => {
    try {
      navigator.clipboard.writeText(text);
      setCopied(id);
      setTimeout(() => setCopied((c) => (c === id ? null : c)), 1200);
    } catch { /* clipboard unavailable outside a secure context */ }
  };

  if (!fields.length && !emptyCount) {
    return (
      <div className="mp-empty">
        {t("meta.empty", { defaultValue: "No metadata available for this document." })}
      </div>
    );
  }

  return (
    <div className="mp">
      <div className="mp-toolbar">
        <div className="mp-search">
          <span className="mp-search-icon" aria-hidden>{Icon.search}</span>
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("meta.filterPlaceholder", { defaultValue: "Filter fields…" })}
            aria-label={t("meta.filterPlaceholder", { defaultValue: "Filter fields…" })}
          />
        </div>
        <button
          type="button"
          className={`mp-tool${jsonMode ? " is-on" : ""}`}
          onClick={() => setJsonMode((m) => !m)}
          title={jsonMode
            ? t("meta.viewList", { defaultValue: "List view" })
            : t("meta.viewJson", { defaultValue: "JSON view" })}
          aria-pressed={jsonMode}
        >
          {jsonMode ? Icon.list : Icon.json}
        </button>
        <button
          type="button"
          className={`mp-tool${copied === "all" ? " is-ok" : ""}`}
          onClick={() => copyText(rawJson, "all")}
          title={t("meta.copyAll", { defaultValue: "Copy all as JSON" })}
        >
          {copied === "all" ? Icon.check : Icon.copy}
        </button>
      </div>

      {q && (
        <div className="mp-note">
          <span>
            {/* `shown`/`total` rather than `count`: keeps i18next's plural
                resolution out of a string that is the same for every count. */}
            {t("meta.matchCount", {
              defaultValue: "{{shown}} of {{total}} fields",
              shown: matches.length,
              total: fields.length,
            })}
          </span>
          <button type="button" className="mp-link" onClick={() => setQuery("")}>
            {t("meta.clearFilter", { defaultValue: "Clear" })}
          </button>
        </div>
      )}

      {jsonMode ? (
        <pre className="mp-json">{rawJson}</pre>
      ) : groups.length === 0 ? (
        <div className="mp-empty">
          {t("meta.noMatches", { defaultValue: "No fields match your filter." })}
        </div>
      ) : (
        groups.map((g) => {
          // A filter hit inside a collapsed group opens it, unless the user said otherwise.
          const open = overrides[g.id] != null ? overrides[g.id] : (q ? true : g.open);
          return (
            <section key={g.id} className={`mp-group${open ? " is-open" : ""}`}>
              <button
                type="button"
                className="mp-group-head"
                onClick={() => setOverrides((o) => ({ ...o, [g.id]: !open }))}
                aria-expanded={open}
              >
                <span className={`mp-chev${open ? " is-open" : ""}`}>{Icon.chevron}</span>
                <span className="mp-group-title">{t(g.i18n, { defaultValue: g.fallback })}</span>
                <span className="mp-count">
                  {q && g.items.length !== g.total ? `${g.items.length}/${g.total}` : g.total}
                </span>
              </button>
              {open && (
                <div className="mp-rows">
                  {g.items.map((f) => (
                    <MetaRow
                      key={f.key}
                      field={f}
                      locale={locale}
                      copied={copied === f.key}
                      onCopy={() => copyText(rawString(f.value), f.key)}
                      copyLabel={t("common.copy", { defaultValue: "Copy" })}
                    />
                  ))}
                </div>
              )}
            </section>
          );
        })
      )}

      {!jsonMode && emptyCount > 0 && (
        <button type="button" className="mp-link mp-empty-toggle" onClick={() => setShowEmpty((s) => !s)}>
          {showEmpty
            ? t("meta.hideEmpty", { defaultValue: "Hide empty fields" })
            : t("meta.showEmpty", { defaultValue: "Show empty fields ({{n}})", n: emptyCount })}
        </button>
      )}
    </div>
  );
}

function MetaRow({ field, locale, copied, onCopy, copyLabel }) {
  const [expanded, setExpanded] = useState(false);
  const fmt = useMemo(
    () => formatValue(field.key, field.value, locale, field.dataType),
    [field.key, field.value, field.dataType, locale],
  );

  const long = !fmt.block && fmt.text.length > 64;
  const shown = fmt.block ? fmt.text : (long && !expanded ? `${fmt.text.slice(0, 64)}…` : fmt.text);

  const valueClass = [
    "mp-v",
    fmt.mono ? "is-mono" : "",
    fmt.muted ? "is-muted" : "",
    fmt.tone ? `mp-chip tone-${fmt.tone}` : "",
    long ? "is-clickable" : "",
  ].filter(Boolean).join(" ");

  return (
    <div className="mp-row">
      {/* title carries the raw column name — the spelling the customer searches by */}
      <div className="mp-k" title={field.key}>{field.label}</div>
      <div className="mp-v-wrap">
        {fmt.block ? (
          <pre className="mp-v is-block">{shown}</pre>
        ) : (
          <span
            className={valueClass}
            title={fmt.hint || (long ? fmt.text : undefined)}
            onClick={long ? () => setExpanded((e) => !e) : undefined}
            role={long ? "button" : undefined}
            tabIndex={long ? 0 : undefined}
            onKeyDown={long ? (e) => {
              if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setExpanded((x) => !x); }
            } : undefined}
          >
            {shown}
          </span>
        )}
      </div>
      <button
        type="button"
        className={`mp-row-copy${copied ? " is-ok" : ""}`}
        onClick={onCopy}
        title={copyLabel}
        aria-label={copyLabel}
      >
        {copied ? Icon.check : Icon.copy}
      </button>
    </div>
  );
}
